/**
 * API client for the Guidance surfaces.
 *
 * Shaped after `contests-service.ts` rather than diverging from it: same auth header,
 * same `{code, detail}` error handling, same guarded JSON parse for the case where a
 * proxy returns HTML instead of the API.
 *
 * Adapter signals do **not** go through here — they go through `gms-adapter.ts`, whose
 * narrow signature is the privacy boundary (decision G3). Keeping them apart is
 * deliberate: this module has generic request helpers, and a generic helper is exactly
 * what would let a payload reach the signal endpoint one day.
 */

import type {
  CompletionResult,
  GmsAccess,
  GmsErrorCode,
  GuideEdit,
  GuideEditorPayload,
  GuideImportResult,
  GuideScreen,
  HelpContext,
  TopicContent,
  LibraryRow,
  PassReason,
  PassResult,
  RecordingPayload,
  WalkthroughDetail,
  WalkthroughProgress,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const BASE = '/api/gms';

/** An API failure carrying the backend's stable error code, when it sent one. */
export class GmsError extends Error {
  readonly code?: GmsErrorCode;
  readonly status: number;
  /** Blocking prerequisites, present on a 409 `prerequisite_failed`. */
  readonly prerequisites?: unknown[];

  constructor(
    message: string,
    status: number,
    code?: GmsErrorCode,
    prerequisites?: unknown[]
  ) {
    super(message);
    this.name = 'GmsError';
    this.status = status;
    this.code = code;
    this.prerequisites = prerequisites;
  }
}

/** The one place the token is read. JSON calls add their Content-Type on top. */
function authHeader(): Record<string, string> {
  return { Authorization: `Token ${localStorage.getItem('wb.authToken')}` };
}

async function describeFailure(response: Response): Promise<GmsError> {
  try {
    const body = await response.json();
    return new GmsError(
      typeof body?.detail === 'string' ? body.detail : `Request failed: ${response.status}`,
      response.status,
      body?.code,
      body?.prerequisites
    );
  } catch {
    return new GmsError(`Request failed: ${response.status}`, response.status);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH';
  params?: URLSearchParams;
  /** Sent as JSON. */
  json?: unknown;
  /** Sent as multipart; the browser sets the boundary, so no Content-Type here. */
  form?: FormData;
  signal?: AbortSignal;
}

/** Every GMS call: auth, the body, and the `{code, detail}` failure. Returns the response. */
async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', params, json, form, signal } = options;
  const query = params?.toString();
  const headers: Record<string, string> = authHeader();
  let body: BodyInit | undefined;
  if (form) {
    body = form;
  } else if (method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(json ?? {});
  }
  const response = await fetch(`${API_BASE_URL}${BASE}${path}${query ? `?${query}` : ''}`, {
    method,
    headers,
    body,
    signal,
  });
  if (!response.ok) throw await describeFailure(response);
  return response;
}

/** The JSON body, or `undefined` for a 204. */
async function readJson<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function getJson<T>(
  path: string,
  params?: URLSearchParams,
  signal?: AbortSignal
): Promise<T> {
  return readJson<T>(await request(path, { params, signal }));
}

async function sendJson<T>(
  method: 'POST' | 'PUT' | 'PATCH',
  path: string,
  json: unknown
): Promise<T> {
  return readJson<T>(await request(path, { method, json }));
}

function postJson<T>(path: string, json: unknown): Promise<T> {
  return sendJson<T>('POST', path, json);
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  return readJson<T>(await request(path, { method: 'POST', form }));
}

/** Capability flags driving the Help action, the route guard and the menu entry. */
export function fetchGmsAccess(signal?: AbortSignal): Promise<GmsAccess> {
  return getJson<GmsAccess>('/my-access/', undefined, signal);
}

/** Screens the header Guide button can appear on — already filtered by the server. */
export async function fetchScreens(signal?: AbortSignal): Promise<GuideScreen[]> {
  const body = await getJson<{ screens: GuideScreen[] }>('/screens/', undefined, signal);
  return body.screens;
}

/** What Help is relevant on the current tool and page. */
export function fetchHelpContext(
  tool: string,
  location?: string,
  signal?: AbortSignal
): Promise<HelpContext> {
  const params = new URLSearchParams({ tool });
  if (location) params.set('location', location);
  return getJson<HelpContext>('/context/', params, signal);
}

/** One topic's published content, with any host-owned notes already resolved. */
export function fetchTopic(
  toolKey: string,
  topicKey: string,
  signal?: AbortSignal
): Promise<TopicContent> {
  return getJson<TopicContent>(`/topics/${toolKey}/${topicKey}/`, undefined, signal);
}

/**
 * The pre-start panel: prerequisites evaluated live, plus progress and XP.
 *
 * `contextIds` carries the sticky host selection the tool already holds — for BPM, the
 * chosen occurrence. Only integer `*_id` values are sent, and the server ignores
 * anything else, so this cannot become a channel for form data.
 */
export function fetchWalkthrough(
  toolKey: string,
  topicKey: string,
  contextIds: Record<string, number> = {},
  signal?: AbortSignal,
  version?: number
): Promise<WalkthroughDetail> {
  const params = new URLSearchParams();
  // A specific version is an author's draft preview (G18); the server refuses it to
  // anyone else.
  if (version) params.set('version', String(version));
  Object.entries(contextIds).forEach(([key, value]) => {
    if (Number.isInteger(value)) params.set(key, String(value));
  });
  return getJson<WalkthroughDetail>(
    `/walkthroughs/${toolKey}/${topicKey}/`,
    params,
    signal
  );
}

/** Validate hard prerequisites and create or resume progress. */
export function startWalkthrough(
  toolKey: string,
  topicKey: string,
  contextIds: Record<string, number> = {},
  isPreview = false,
  version?: number
): Promise<WalkthroughProgress> {
  return postJson<WalkthroughProgress>(`/walkthroughs/${toolKey}/${topicKey}/start/`, {
    context_ids: contextIds,
    is_preview: isPreview,
    ...(version ? { version } : {}),
  });
}

/** Abandon the attempt and begin again. Host records are never touched. */
export function restartWalkthrough(
  toolKey: string,
  topicKey: string,
  contextIds: Record<string, number> = {}
): Promise<WalkthroughProgress> {
  return postJson<WalkthroughProgress>(`/walkthroughs/${toolKey}/${topicKey}/restart/`, {
    context_ids: contextIds,
  });
}

/** Record a voluntary exit. */
export function endWalkthrough(
  toolKey: string,
  topicKey: string,
  reason = ''
): Promise<void> {
  return postJson<void>(`/walkthroughs/${toolKey}/${topicKey}/end/`, { reason });
}

/** Confirm the final action succeeded, and collect XP if any is due. */
export function completeWalkthrough(
  toolKey: string,
  topicKey: string,
  signal: string
): Promise<CompletionResult> {
  return postJson<CompletionResult>(`/walkthroughs/${toolKey}/${topicKey}/complete/`, {
    signal,
  });
}

/**
 * Move past the current step without claiming its action happened — Next on a done,
 * optional or look-only step, Skip on a recorded step whose element has gone. The
 * server checks the reason; on the final step it finishes the walkthrough itself.
 */
export function passStep(
  toolKey: string,
  topicKey: string,
  stepKey: string,
  reason: PassReason,
  contextIds: Record<string, number> = {}
): Promise<PassResult> {
  return postJson<PassResult>(`/walkthroughs/${toolKey}/${topicKey}/pass/`, {
    step_key: stepKey,
    reason,
    context_ids: contextIds,
  });
}

/** Create or update this user's single rating for this walkthrough version. */
export function saveFeedback(
  toolKey: string,
  topicKey: string,
  rating: number,
  comment: string
): Promise<void> {
  return sendJson<void>('PUT', `/walkthroughs/${toolKey}/${topicKey}/feedback/`, {
    rating,
    comment,
  });
}

/** The content library, or the review queue when a status is given. */
export async function fetchLibrary(
  status?: string,
  signal?: AbortSignal
): Promise<LibraryRow[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const body = await getJson<{ results: LibraryRow[] }>('/manage/topics/', params, signal);
  return body.results;
}

/** Submit, approve or reject one revision. */
export function transitionRevision(
  revisionId: number,
  action: 'submit' | 'approve' | 'reject',
  comment = '',
  screenshotsChecked = false
): Promise<void> {
  // Approving a guide with screenshots needs the approver's confirmation that they
  // checked every one for personal details; the server refuses without it (G20).
  const body =
    action === 'approve'
      ? { comment, screenshots_checked: screenshotsChecked }
      : { comment };
  return postJson<void>(`/manage/revisions/${revisionId}/${action}/`, body);
}

/**
 * Publish a revision over whatever is currently live.
 *
 * `expectedTopicRevision` is required in practice: the backend treats a missing token
 * as a conflict rather than as consent (decision C8's rule, carried into GMS).
 */
export function publishRevision(
  revisionId: number,
  expectedTopicRevision: number,
  publishNote = ''
): Promise<void> {
  return postJson<void>(`/manage/revisions/${revisionId}/publish/`, {
    expected_topic_revision: expectedTopicRevision,
    publish_note: publishNote,
  });
}

/** Restore whatever was published before the current revision. */
export function rollbackTopic(
  toolKey: string,
  topicKey: string,
  comment = ''
): Promise<void> {
  return postJson<void>(`/manage/topics/${toolKey}/${topicKey}/rollback/`, { comment });
}

// -- Review & Edit (G18) ------------------------------------------------------

function guidePath(toolKey: string, topicKey: string): string {
  return `/manage/guides/${encodeURIComponent(toolKey)}/${encodeURIComponent(topicKey)}/`;
}

function guideScreenshotPath(toolKey: string, topicKey: string, stepKey: string): string {
  return `${guidePath(toolKey, topicKey)}steps/${encodeURIComponent(stepKey)}/screenshot/`;
}

/** One guide's live and draft state, and what the draft changes. */
export function fetchGuideEditor(
  toolKey: string,
  topicKey: string,
  signal?: AbortSignal
): Promise<GuideEditorPayload> {
  return getJson<GuideEditorPayload>(guidePath(toolKey, topicKey), undefined, signal);
}

/** Open a draft to edit, copying the live version. A live version is never edited. */
export function beginGuideEdit(
  toolKey: string,
  topicKey: string
): Promise<GuideEditorPayload> {
  return postJson<GuideEditorPayload>(`${guidePath(toolKey, topicKey)}edit/`, {});
}

/** Save the draft. `expectedRevision` is the draft revision's token; stale is a 409. */
export function updateGuide(
  toolKey: string,
  topicKey: string,
  expectedRevision: number,
  edit: GuideEdit
): Promise<GuideEditorPayload> {
  return sendJson<GuideEditorPayload>('PATCH', guidePath(toolKey, topicKey), {
    expected_revision: expectedRevision,
    ...edit,
  });
}

/** Replace one draft step's screenshot with a PNG. */
export function replaceGuideScreenshot(
  toolKey: string,
  topicKey: string,
  stepKey: string,
  expectedRevision: number,
  file: File
): Promise<GuideEditorPayload> {
  const form = new FormData();
  form.set('expected_revision', String(expectedRevision));
  form.set('screenshot', file);
  return postForm<GuideEditorPayload>(guideScreenshotPath(toolKey, topicKey, stepKey), form);
}

/** Publish the approved text and the draft steps together, or neither. */
export function publishGuide(
  toolKey: string,
  topicKey: string,
  expectedTopicRevision: number,
  publishNote = ''
): Promise<GuideEditorPayload> {
  return postJson<GuideEditorPayload>(`${guidePath(toolKey, topicKey)}publish/`, {
    expected_topic_revision: expectedTopicRevision,
    publish_note: publishNote,
  });
}

/**
 * Upload a guide recorded in the app (G19): the recording as JSON and one PNG per step.
 * Creates drafts only; nothing is published.
 */
export function recordGuide(
  recording: RecordingPayload,
  screenshots: Record<string, File>
): Promise<GuideImportResult> {
  const form = new FormData();
  form.set('guide', JSON.stringify(recording));
  Object.entries(screenshots).forEach(([stepKey, file]) => {
    form.set(`screenshot.${stepKey}`, file);
  });
  return postForm<GuideImportResult>('/manage/guides/record/', form);
}

/**
 * A draft step's screenshot as bytes, for the blur tool (G20). Fetched through the API,
 * not the signed URL: drawing on a canvas needs CORS that the bucket does not grant.
 */
export async function fetchGuideScreenshot(
  toolKey: string,
  topicKey: string,
  stepKey: string,
  signal?: AbortSignal
): Promise<Blob> {
  return (await request(guideScreenshotPath(toolKey, topicKey, stepKey), { signal })).blob();
}
